import React, { useState } from 'react';
import { InlineSpinner } from '../../common/loading';

import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  FormHelperText,
  Autocomplete,
  Chip,
alpha,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Button,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption } from '../../../services/api';
import { FormData } from './types';
import { getProjectStatusColor, filterProjectsByStatus, isSolutionStoryFirstThreeFilled, TECH_STACK_MAX_ITEMS } from './utils';
import { useSolutionStoryOptions } from '../../solution-stories/useSolutionStoryOptions';

const PLATFORM_MAX_ITEMS = 3;

interface SolutionStoryFormProps {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | number | null | File | File[] | string[]) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  projectOptions: ProjectDropdownOption[];
  projectsLoading: boolean;
  projectStatusFilter: string | null;
  onProjectStatusFilterChange: (filter: string | null) => void;
  selectedProject: ProjectDropdownOption | null;
  onProjectSelect: (project: ProjectDropdownOption | null) => void;
  isFormDisabled: boolean;
}

export const SolutionStoryForm: React.FC<SolutionStoryFormProps> = ({
  formData,
  onInputChange,
  onFileChange,
  projectOptions,
  projectsLoading,
  projectStatusFilter,
  onProjectStatusFilterChange,
  selectedProject,
  onProjectSelect,
  isFormDisabled,
}) => {
  const filteredProjectOptions = filterProjectsByStatus(projectOptions, projectStatusFilter);

  const { practiceOptions, industryOptions, platformOptions, techStackOptions } = useSolutionStoryOptions();

  // Track which fields have been touched (focused and blurred)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [techStackLimitHit, setTechStackLimitHit] = useState(false);

  const handleBlur = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
  };

  const shouldShowError = (fieldName: string, isEmpty: boolean) => {
    return touchedFields.has(fieldName) && isEmpty;
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isFormDisabled ? alpha(SEARCE_COLORS.neutral_200, 0.5) : SEARCE_COLORS.cardBackground,
      transition: 'background-color 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isFormDisabled ? SEARCE_COLORS.border : SEARCE_COLORS.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: SEARCE_COLORS.primary,
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: SEARCE_COLORS.primary,
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Important Information Section */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${SEARCE_COLORS.border}`,
            borderRadius: 2,
            backgroundColor: SEARCE_COLORS.cardBackground,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: SEARCE_COLORS.textPrimary,
              mb: 1,
              textDecoration: 'underline',
            }}
          >
            IMPORTANT
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              Fields marked with red asterisks(*) are mandatory fields and requires an user input
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Project Selection Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
          }}
        >
          Project Selection
        </Typography>
      </Grid>

      {/* Project Code - Dynamic Dropdown */}
      <Grid item xs={12}>
        <Autocomplete
          options={filteredProjectOptions}
          getOptionLabel={(option) => option.display_label}
          value={selectedProject}
          onChange={(_, newValue) => {
            onProjectSelect(newValue);
            if (!newValue) {
              handleBlur('projectCode');
            }
          }}
          onBlur={() => handleBlur('projectCode')}
          loading={projectsLoading}
          isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Code"
              required
              helperText="Select from your projects"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {projectsLoading ? <InlineSpinner size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.project_code}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
                    {option.project_code}
                  </Typography>
                  {option.status && (
                    <Chip
                      label={option.status}
                      size="small"
                      sx={{
                        backgroundColor: getProjectStatusColor(option.status),
                        color: SEARCE_COLORS.white,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary }}>
                  {option.project_name}
                </Typography>
              </Box>
            </Box>
          )}
          disabled={isFormDisabled}
        />
        {shouldShowError('projectCode', !selectedProject) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Project Code is required
          </FormHelperText>
        )}
      </Grid>

      {/* Auto-filled fields from project selection - First Row */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Account Name"
          value={formData.accountName || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Opportunity"
          value={formData.opportunity || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Workload"
          value={formData.workload || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>

      {/* Auto-filled fields from project selection - Second Row */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Delivery BU"
          value={formData.businessUnit || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Delivery SBU"
          value={formData.deliverySubBu || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Project Owner"
          value={formData.opportunityProjectOwner || ''}
          disabled
          helperText={selectedProject ? "Auto-filled from project" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Project Owner Email"
          type="email"
          value={formData.opportunityProjectOwnerEmail || ''}
          disabled
          helperText={selectedProject ? "From project tracker (not your login email)" : "Select a project to auto-fill"}
          sx={textFieldSx}
        />
      </Grid>

      {/* Solution Story Fields */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: SEARCE_COLORS.textPrimary,
            mt: 2,
          }}
        >
          Solution Story Details
        </Typography>
      </Grid>

      {/* Solution Provided - Max 70 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Solution Provided"
          value={formData.solutionProvided}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 70) {
              onInputChange('solutionProvided', value);
            }
          }}
          onBlur={() => handleBlur('solutionProvided')}
          placeholder="Enter solution provided (max 70 characters)"
          required
          inputProps={{ maxLength: 70 }}
          helperText={`${formData.solutionProvided.length}/70 characters`}
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError('solutionProvided', !formData.solutionProvided.trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Solution Provided is required
          </FormHelperText>
        )}
      </Grid>

      {/* Key Objectives/Challenges - Min 400 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Key Objectives/Challenges"
          value={formData.keyObjectivesChallenges}
          onChange={(e) => {
            onInputChange('keyObjectivesChallenges', e.target.value);
          }}
          onBlur={() => handleBlur('keyObjectivesChallenges')}
          placeholder="Enter key objectives and challenges (min 400 characters)"
          required
          multiline
          rows={4}
          helperText={`${formData.keyObjectivesChallenges.length} characters (minimum 400)`}
          error={touchedFields.has('keyObjectivesChallenges') && formData.keyObjectivesChallenges.length > 0 && formData.keyObjectivesChallenges.length < 400}
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError('keyObjectivesChallenges', !formData.keyObjectivesChallenges.trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Key Objectives/Challenges is required
          </FormHelperText>
        )}
        {touchedFields.has('keyObjectivesChallenges') && formData.keyObjectivesChallenges.trim().length > 0 && formData.keyObjectivesChallenges.length < 400 && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Key Objectives/Challenges must be at least 400 characters ({400 - formData.keyObjectivesChallenges.length} more needed)
          </FormHelperText>
        )}
      </Grid>

      {/* Executive Summary - Max 150 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Executive Summary"
          value={formData.executiveSummary}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 150) {
              onInputChange('executiveSummary', value);
            }
          }}
          onBlur={() => handleBlur('executiveSummary')}
          placeholder="Enter executive summary (max 150 characters)"
          required
          multiline
          rows={3}
          inputProps={{ maxLength: 150 }}
          helperText={`${formData.executiveSummary.length}/150 characters`}
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError('executiveSummary', !formData.executiveSummary.trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Executive Summary is required
          </FormHelperText>
        )}
      </Grid>

      {/* Business Context and Solution - Side by Side */}
      <Grid item xs={12} md={6}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              mb: 1.5,
            }}
          >
            Business Context <span style={{ color: SEARCE_COLORS.error }}>*</span>
          </Typography>
          {[0, 1, 2, 3].map((index) => {
            const currentValue = formData.businessContext?.[index] || '';
            const totalChars = (formData.businessContext || []).reduce((sum, val) => sum + (val?.length || 0), 0);
            return (
              <TextField
                key={index}
                fullWidth
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const currentArray = [...(formData.businessContext || ['', '', '', ''])];
                  const newTotal = totalChars - (currentValue?.length || 0) + newValue.length;
                  if (newTotal <= 358) {
                    currentArray[index] = newValue;
                    onInputChange('businessContext', currentArray);
                  }
                }}
                onBlur={() => handleBlur('businessContext')}
                placeholder={`Point ${index + 1}`}
                size="small"
                multiline
                minRows={2}
                maxRows={15}
                sx={{ 
                  ...textFieldSx, 
                  mb: 1.5,
                  '& .MuiInputBase-input': {
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'auto',
                  },
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
                disabled={isFormDisabled}
              />
            );
          })}
          <Box sx={{ mt: 1 }}>
            <FormHelperText sx={{ color: SEARCE_COLORS.textSecondary }}>
              Total: {(formData.businessContext || []).reduce((sum, val) => sum + (val?.length || 0), 0)}/358 characters
            </FormHelperText>
            {shouldShowError('businessContext', !isSolutionStoryFirstThreeFilled(formData.businessContext)) && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                First 3 points are required (4th is optional)
              </FormHelperText>
            )}
            {(formData.businessContext || []).reduce((sum, val) => sum + (val?.length || 0), 0) > 358 && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                Total characters cannot exceed 358
              </FormHelperText>
            )}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              mb: 1.5,
            }}
          >
            Solution <span style={{ color: SEARCE_COLORS.error }}>*</span>
          </Typography>
          {[0, 1, 2, 3].map((index) => {
            const currentValue = formData.solution?.[index] || '';
            const totalChars = (formData.solution || []).reduce((sum, val) => sum + (val?.length || 0), 0);
            return (
              <TextField
                key={index}
                fullWidth
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const currentArray = [...(formData.solution || ['', '', '', ''])];
                  const newTotal = totalChars - (currentValue?.length || 0) + newValue.length;
                  if (newTotal <= 358) {
                    currentArray[index] = newValue;
                    onInputChange('solution', currentArray);
                  }
                }}
                onBlur={() => handleBlur('solution')}
                placeholder={`Point ${index + 1}`}
                size="small"
                multiline
                minRows={2}
                maxRows={15}
                sx={{ 
                  ...textFieldSx, 
                  mb: 1.5,
                  '& .MuiInputBase-input': {
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'auto',
                  },
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
                disabled={isFormDisabled}
              />
            );
          })}
          <Box sx={{ mt: 1 }}>
            <FormHelperText sx={{ color: SEARCE_COLORS.textSecondary }}>
              Total: {(formData.solution || []).reduce((sum, val) => sum + (val?.length || 0), 0)}/358 characters
            </FormHelperText>
            {shouldShowError('solution', !isSolutionStoryFirstThreeFilled(formData.solution)) && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                First 3 points are required (4th is optional)
              </FormHelperText>
            )}
            {(formData.solution || []).reduce((sum, val) => sum + (val?.length || 0), 0) > 358 && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                Total characters cannot exceed 358
              </FormHelperText>
            )}
          </Box>
        </Box>
      </Grid>

      {/* Challenges & whether these changed since inception - Min 250 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Challenges & whether these changed since inception"
          value={formData.challengesChangedSinceInception || ''}
          onChange={(e) => onInputChange('challengesChangedSinceInception', e.target.value)}
          onBlur={() => handleBlur('challengesChangedSinceInception')}
          placeholder="Enter challenges and whether they changed since inception (minimum 250 characters)"
          required
          multiline
          rows={4}
          helperText={`${(formData.challengesChangedSinceInception || '').length} characters (minimum 250)`}
          error={
            touchedFields.has('challengesChangedSinceInception') &&
            (formData.challengesChangedSinceInception || '').trim().length > 0 &&
            (formData.challengesChangedSinceInception || '').trim().length < 250
          }
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError(
          'challengesChangedSinceInception',
          (formData.challengesChangedSinceInception || '').trim().length < 250
        ) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {(formData.challengesChangedSinceInception || '').trim().length === 0
              ? 'Must be at least 250 characters'
              : `${250 - (formData.challengesChangedSinceInception || '').trim().length} more characters needed (minimum 250)`}
          </FormHelperText>
        )}
      </Grid>

      {/* Solution description - Min 400 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Solution description"
          value={formData.solutionDescription || ''}
          onChange={(e) => onInputChange('solutionDescription', e.target.value)}
          onBlur={() => handleBlur('solutionDescription')}
          placeholder="Enter solution description (minimum 400 characters)"
          required
          multiline
          rows={5}
          helperText={`${(formData.solutionDescription || '').length} characters (minimum 400)`}
          error={
            touchedFields.has('solutionDescription') &&
            (formData.solutionDescription || '').trim().length > 0 &&
            (formData.solutionDescription || '').trim().length < 400
          }
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError(
          'solutionDescription',
          (formData.solutionDescription || '').trim().length < 400
        ) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {(formData.solutionDescription || '').trim().length === 0
              ? 'Must be at least 400 characters'
              : `${400 - (formData.solutionDescription || '').trim().length} more characters needed (minimum 400)`}
          </FormHelperText>
        )}
      </Grid>

      {/* Practice - Dropdown */}
      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          size="small"
          required
          disabled={isFormDisabled}
          sx={textFieldSx}
        >
          <InputLabel id="practice-label">Practice</InputLabel>
          <Select
            labelId="practice-label"
            value={formData.practice || ''}
            label="Practice"
            onChange={(e) => onInputChange('practice', e.target.value)}
            onBlur={() => handleBlur('practice')}
          >
            {practiceOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {shouldShowError('practice', !formData.practice) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Practice is required
          </FormHelperText>
        )}
      </Grid>

      {/* Platform - Dropdown */}
      <Grid item xs={12} md={6}>
        <FormControl 
          fullWidth 
          size="small" 
          required
          disabled={isFormDisabled}
          sx={textFieldSx}
        >
          <InputLabel id="platform-label">Platform</InputLabel>
          <Select
            labelId="platform-label"
            multiple
            value={formData.platform || []}
            label="Platform"
            onChange={(e) => {
              const value = e.target.value;
              const next = typeof value === 'string' ? value.split(',') : value;
              onInputChange('platform', next.slice(0, PLATFORM_MAX_ITEMS));
            }}
            onBlur={() => handleBlur('platform')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {platformOptions.map((option) => {
              const isSelected = (formData.platform || []).includes(option);
              const atMax = (formData.platform || []).length >= PLATFORM_MAX_ITEMS;
              return (
                <MenuItem key={option} value={option} disabled={!isSelected && atMax}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormHelperText sx={{ mt: 0.5 }}>
          {(formData.platform || []).length}/{PLATFORM_MAX_ITEMS} selected (maximum {PLATFORM_MAX_ITEMS})
        </FormHelperText>
        {shouldShowError('platform', !(formData.platform && formData.platform.length > 0)) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Platform is required
          </FormHelperText>
        )}
      </Grid>

      {/* In what way was the platform used? - Min 100 characters */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="In what way was the platform used? *"
          value={formData.platformUsageWay || ''}
          onChange={(e) => onInputChange('platformUsageWay', e.target.value)}
          onBlur={() => handleBlur('platformUsageWay')}
          placeholder="Enter how the platform was used (minimum 100 characters)"
          required
          multiline
          minRows={2}
          helperText={`${(formData.platformUsageWay || '').length} characters (minimum 100)`}
          error={
            touchedFields.has('platformUsageWay') &&
            (formData.platformUsageWay || '').trim().length > 0 &&
            (formData.platformUsageWay || '').trim().length < 100
          }
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError(
          'platformUsageWay',
          (formData.platformUsageWay || '').trim().length < 100
        ) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {(formData.platformUsageWay || '').trim().length === 0
              ? 'Must be at least 100 characters'
              : `${100 - (formData.platformUsageWay || '').trim().length} more characters needed (minimum 100)`}
          </FormHelperText>
        )}
      </Grid>

      {/* Impact - Full Width */}
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              mb: 1.5,
            }}
          >
            Impact <span style={{ color: SEARCE_COLORS.error }}>*</span>
          </Typography>
          {[0, 1, 2, 3].map((index) => {
            const currentValue = formData.impact?.[index] || '';
            const totalChars = (formData.impact || []).reduce((sum, val) => sum + (val?.length || 0), 0);
            return (
              <TextField
                key={index}
                fullWidth
                value={currentValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const currentArray = [...(formData.impact || ['', '', '', ''])];
                  const newTotal = totalChars - (currentValue?.length || 0) + newValue.length;
                  if (newTotal <= 358) {
                    currentArray[index] = newValue;
                    onInputChange('impact', currentArray);
                  }
                }}
                onBlur={() => handleBlur('impact')}
                placeholder={`Point ${index + 1}`}
                size="small"
                multiline
                minRows={2}
                maxRows={15}
                sx={{ 
                  ...textFieldSx, 
                  mb: 1.5,
                  '& .MuiInputBase-input': {
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'auto',
                  },
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
                disabled={isFormDisabled}
              />
            );
          })}
          <Box sx={{ mt: 1 }}>
            <FormHelperText sx={{ color: SEARCE_COLORS.textSecondary }}>
              Total: {(formData.impact || []).reduce((sum, val) => sum + (val?.length || 0), 0)}/358 characters
            </FormHelperText>
            {shouldShowError('impact', !isSolutionStoryFirstThreeFilled(formData.impact)) && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                First 3 points are required (4th is optional)
              </FormHelperText>
            )}
            {(formData.impact || []).reduce((sum, val) => sum + (val?.length || 0), 0) > 358 && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                Total characters cannot exceed 358
              </FormHelperText>
            )}
          </Box>
        </Box>
      </Grid>

      {/* Detailed solution impact - Min 400 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Detailed solution impact *"
          value={formData.detailedSolutionImpact || ''}
          onChange={(e) => onInputChange('detailedSolutionImpact', e.target.value)}
          onBlur={() => handleBlur('detailedSolutionImpact')}
          placeholder="Enter detailed solution impact (minimum 400 characters)"
          required
          multiline
          rows={5}
          helperText={`${(formData.detailedSolutionImpact || '').length} characters (minimum 400)`}
          error={
            touchedFields.has('detailedSolutionImpact') &&
            (formData.detailedSolutionImpact || '').trim().length > 0 &&
            (formData.detailedSolutionImpact || '').trim().length < 400
          }
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError(
          'detailedSolutionImpact',
          (formData.detailedSolutionImpact || '').trim().length < 400
        ) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {(formData.detailedSolutionImpact || '').trim().length === 0
              ? 'Must be at least 400 characters'
              : `${400 - (formData.detailedSolutionImpact || '').trim().length} more characters needed (minimum 400)`}
          </FormHelperText>
        )}
      </Grid>

      {/* Tech Stack - chip input, one array element per technology (matches reviewer's tag display) */}
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              mb: 1.5,
            }}
          >
            Tech Stack <span style={{ color: SEARCE_COLORS.error }}>*</span>
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={techStackOptions}
            value={(formData.techStack || []).filter((v) => v?.trim())}
            onChange={(_e, newValue) => {
              const next = (newValue as string[]).map((s) => s.trim()).filter(Boolean);
              if (next.length > TECH_STACK_MAX_ITEMS) {
                setTechStackLimitHit(true);
                return;
              }
              setTechStackLimitHit(false);
              onInputChange('techStack', next);
            }}
            onBlur={() => handleBlur('techStack')}
            disabled={isFormDisabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select from configured options or type to add"
                sx={textFieldSx}
              />
            )}
          />
          <FormHelperText sx={{ color: SEARCE_COLORS.textSecondary, mt: 0.5 }}>
            {(formData.techStack || []).filter((v) => v?.trim()).length}/{TECH_STACK_MAX_ITEMS} items (maximum {TECH_STACK_MAX_ITEMS})
          </FormHelperText>
          {techStackLimitHit && (
            <FormHelperText error sx={{ mt: 0.5 }}>
              Tech Stack: Maximum {TECH_STACK_MAX_ITEMS} items allowed
            </FormHelperText>
          )}
          {shouldShowError('techStack', (formData.techStack || []).filter((val) => val?.trim()).length === 0) && (
            <FormHelperText error sx={{ mt: 0.5 }}>
              Tech Stack: At least one entry is required
            </FormHelperText>
          )}
        </Box>
      </Grid>

      {/* In what way was each specific technology used - Min 100 characters */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="In what way was each specific technology used *"
          value={formData.techUsageWay || ''}
          onChange={(e) => onInputChange('techUsageWay', e.target.value)}
          onBlur={() => handleBlur('techUsageWay')}
          placeholder="Enter how each specific technology was used (minimum 100 characters)"
          required
          multiline
          minRows={2}
          helperText={`${(formData.techUsageWay || '').length} characters (minimum 100)`}
          error={
            touchedFields.has('techUsageWay') &&
            (formData.techUsageWay || '').trim().length > 0 &&
            (formData.techUsageWay || '').trim().length < 100
          }
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError(
          'techUsageWay',
          (formData.techUsageWay || '').trim().length < 100
        ) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            {(formData.techUsageWay || '').trim().length === 0
              ? 'Must be at least 100 characters'
              : `${100 - (formData.techUsageWay || '').trim().length} more characters needed (minimum 100)`}
          </FormHelperText>
        )}
      </Grid>

      {/* Referencable / Non-referenceable - Dropdown */}
      <Grid item xs={12}>
        <FormControl 
          fullWidth 
          size="small" 
          required
          disabled={isFormDisabled}
          sx={textFieldSx}
        >
          <InputLabel id="referencable-label">Referenceable / Non-referenceable</InputLabel>
          <Select
            labelId="referencable-label"
            value={formData.referencable || ''}
            label="Referencable / Non-referenceable"
            onChange={(e) => onInputChange('referencable', e.target.value)}
            onBlur={() => handleBlur('referencable')}
          >
            <MenuItem value="R">R (Referenceable)</MenuItem>
            <MenuItem value="NR">NR (Non-referencable)</MenuItem>
          </Select>
        </FormControl>
        {shouldShowError('referencable', !formData.referencable) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Referencable / Non-referenceable is required
          </FormHelperText>
        )}
      </Grid>

      {/* Industry Group and Industry - Side by Side */}
      <Grid item xs={12} md={6}>
        <FormControl 
          fullWidth 
          size="small" 
          required
          disabled={isFormDisabled}
          sx={textFieldSx}
        >
          <InputLabel id="industry-group-label">Industry Group</InputLabel>
          <Select
            labelId="industry-group-label"
            value={formData.industryGroup || ''}
            label="Industry Group"
            onChange={(e) => onInputChange('industryGroup', e.target.value)}
            onBlur={() => handleBlur('industryGroup')}
          >
            {industryOptions.map((industry) => (
              <MenuItem key={industry} value={industry}>
                {industry}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {shouldShowError('industryGroup', !formData.industryGroup) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Industry Group is required
          </FormHelperText>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Industry"
          value={formData.industry || ''}
          onChange={(e) => onInputChange('industry', e.target.value)}
          onBlur={() => handleBlur('industry')}
          placeholder="Enter industry"
          required
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
        {shouldShowError('industry', !formData.industry?.trim()) && (
          <FormHelperText error sx={{ mt: 0.5 }}>
            Industry is required
          </FormHelperText>
        )}
      </Grid>

      {/* Teamwork & Collaboration - Optional */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Teamwork & Collaboration"
          value={formData.teamworkCollaboration || ''}
          onChange={(e) => onInputChange('teamworkCollaboration', e.target.value)}
          placeholder="Enter teamwork & collaboration details (optional)"
          multiline
          rows={4}
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
      </Grid>

      {/* Results & Business Impact - Optional */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Results & Business Impact"
          value={formData.resultsBusinessImpact || ''}
          onChange={(e) => onInputChange('resultsBusinessImpact', e.target.value)}
          placeholder="Enter results & business impact details (optional)"
          multiline
          rows={4}
          sx={textFieldSx}
          disabled={isFormDisabled}
        />
      </Grid>

      {/* File Upload - Multiple Files */}
      <Grid item xs={12}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: SEARCE_COLORS.textPrimary, mb: 1 }}
        >
          Attach Files
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            disabled={isFormDisabled}
            sx={{
              borderColor: SEARCE_COLORS.border,
              color: SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
              },
              '&:disabled': {
                borderColor: alpha(SEARCE_COLORS.border, 0.5),
                color: alpha(SEARCE_COLORS.textSecondary, 0.5),
              },
            }}
          >
            Choose files
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls,.csv"
              onChange={onFileChange}
            />
          </Button>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            {formData.solutionStoryFiles && formData.solutionStoryFiles.length > 0
              ? `${formData.solutionStoryFiles.length} file(s) selected`
              : 'No files chosen'}
          </Typography>
        </Box>
        {formData.solutionStoryFiles && formData.solutionStoryFiles.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {formData.solutionStoryFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  mb: 1,
                  border: `1px solid ${SEARCE_COLORS.border}`,
                  borderRadius: 1,
                  backgroundColor: SEARCE_COLORS.cardBackground,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <AttachFileIcon sx={{ fontSize: 18, color: SEARCE_COLORS.textSecondary }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: SEARCE_COLORS.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: SEARCE_COLORS.textSecondary, ml: 1 }}
                  >
                    ({(file.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newFiles = [...formData.solutionStoryFiles];
                    newFiles.splice(index, 1);
                    onInputChange('solutionStoryFiles', newFiles);
                  }}
                  disabled={isFormDisabled}
                  sx={{
                    color: SEARCE_COLORS.error,
                    '&:hover': {
                      backgroundColor: alpha(SEARCE_COLORS.error, 0.1),
                    },
                    '&:disabled': {
                      color: alpha(SEARCE_COLORS.textSecondary, 0.3),
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <FormHelperText sx={{ mt: 1 }}>
          Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG, XLSX, XLS, CSV (Max 10MB per file)
        </FormHelperText>
        <FormHelperText sx={{ mt: 0.5, color: SEARCE_COLORS.error }}>
          Note: Files are not saved in drafts. If you open a draft, please re-upload attachments before submitting.
        </FormHelperText>
      </Grid>
    </Grid>
  );
};
